/* ============================================================
 * 消息中心 store — 仅缓存未读数（列表走组件本地状态）
 *
 * 60 秒内复用 GET /app/msg/unread-count 结果，避免每次进页面都拉。
 * 读 / 删 / 全部已读走本地乐观更新，避免额外网络抖动；失败由调用方回滚。
 *
 * 注意：公告（/app/notice/list）**不**进未读数，独立走 notice store。
 * ============================================================ */

import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { getUnreadCount } from '@/api/message'
import type { ApiError } from '@/types/api'

const TTL_MS = 60 * 1000
const POLLING_DEFAULT_MS = 60_000

let messageListenersRegistered = false

export const useMessageStore = defineStore('message', () => {
  const unreadCount = ref(0)
  const fetchedAt = ref(0)
  /** 防重入：未导出，避免外部依赖；仅 load() / reset() 内部使用。 */
  const inflight = ref(false)
  const pollingTimer = ref<number | null>(null)

  const isStale = computed(() => Date.now() - fetchedAt.value > TTL_MS)

  async function load(force = false): Promise<void> {
    if (inflight.value) return
    if (!force && fetchedAt.value > 0 && !isStale.value) return
    inflight.value = true
    try {
      const resp = await getUnreadCount()
      unreadCount.value = Number(resp.data ?? 0)
      fetchedAt.value = Date.now()
    } catch (err) {
      // 未登录 / token 失效会被 axios 拦截器抛 ApiError；此处静默吞掉，不污染 UI。
      void err as unknown as ApiError
    } finally {
      inflight.value = false
    }
  }

  /** 乐观更新：标记单条已读时调用。 */
  function decrement(by = 1): void {
    unreadCount.value = Math.max(0, unreadCount.value - by)
  }

  /** 乐观更新：全部已读时调用。 */
  function clearUnread(): void {
    unreadCount.value = 0
  }

  function reset(): void {
    unreadCount.value = 0
    fetchedAt.value = 0
    inflight.value = false
  }

  function startPolling(interval: number = POLLING_DEFAULT_MS): void {
    if (typeof window === 'undefined') return
    if (pollingTimer.value !== null) return
    void load(true)
    pollingTimer.value = window.setInterval(() => {
      void load(true)
    }, interval)
  }

  function stopPolling(): void {
    if (pollingTimer.value === null) return
    if (typeof window !== 'undefined') {
      window.clearInterval(pollingTimer.value)
    }
    pollingTimer.value = null
  }

  if (typeof window !== 'undefined' && !messageListenersRegistered) {
    window.addEventListener('xuntu:auth-expired', () => {
      const store = useMessageStore()
      store.stopPolling()
      store.reset()
    })
    messageListenersRegistered = true
  }

  return {
    unreadCount,
    load,
    startPolling,
    stopPolling,
    decrement,
    clearUnread,
    reset,
  }
})
